import type { LandingPageBody, LandingPageParams } from "@apptus/esales-api";
import { useMarket } from "@context/marketContext";
import getLandingPage from "@server/requests/elevate/getLandingPage";
import mapNgFilters from "@server/requests/elevate/mapNgFilters";
import mapNgSort from "@server/requests/elevate/mapNgSort";
import { fetchCategory } from "@server/requests/product";
import type {
  Filter,
  Loop54Content,
  Loop54State,
} from "@server/utils/parseLoop/types";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import type { Sort } from "types/common";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import { PRODUCTS_PER_PAGE } from "@/config/products";
import { generateHash, getInitialParams } from "@/utils/productSearch";

const useProductListing = ({
  initialData,
  pageNumber = 1,
  params,
  body,
}: {
  initialData?: Loop54Content;
  pageNumber?: number;
  params: LandingPageParams;
  body?: LandingPageBody;
}) => {
  const router = useRouter();
  const marketConfig = useMarket().state.market;
  const [state, setState] = useState<Loop54State | undefined>();
  const [data, setData] = useState<Loop54Content | undefined>(initialData);

  const getCategoryPayload = useCallback(() => {
    const { sort, filters } = getInitialParams(initialData);
    return {
      ...params,
      sort: mapNgSort(sort),
      limit: PRODUCTS_PER_PAGE,
      skip: PRODUCTS_PER_PAGE * (pageNumber - 1),
      facets: { ...params.facets, ...mapNgFilters(filters) },
    };
  }, [initialData, pageNumber, params]);

  const onChange = useCallback(() => {
    const { sort, filters } = getInitialParams(initialData);
    const payload = getCategoryPayload();

    if (FEATURE_FLAGS.elevate) {
      getLandingPage(marketConfig, payload as LandingPageParams, body)
        .then(setData)
        .catch(() => console.error("Failed to fetch category"));

      setState({ sort, filters });
    } else {
      fetchCategory(payload as any)
        .then(setData)
        .catch(() => console.error("Failed to fetch category"));
    }

    setState({ sort, filters });
  }, [initialData, getCategoryPayload, marketConfig, body]);

  const updateFilter = (payload: Filter[]) => {
    const { sort } = getInitialParams(initialData);

    if (pageNumber && pageNumber > 1) {
      router.push(generateHash(sort, payload));
    } else {
      window.history.replaceState(
        window.history.state,
        "",
        generateHash(sort, payload),
      );
      onChange();
    }
  };

  const updateSort = (payload: Sort) => {
    const { filters } = getInitialParams(initialData);

    if (pageNumber && pageNumber > 1) {
      router.push(generateHash(payload, filters));
    } else {
      window.history.replaceState(
        window.history.state,
        "",
        generateHash(payload, filters),
      );
      onChange();
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash.length) {
      onChange();
    }
  }, [onChange, initialData]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const payload = getCategoryPayload();

    void getLandingPage(marketConfig, payload as LandingPageParams, body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    state,
    updateSort,
    updateFilter,
  };
};

export default useProductListing;
